import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import { adaptOptimTurpeV2Etude } from "@modules/courtage/utils/optim-turpe-v2.adapter"
import type {
    OptimTurpeV2Cycle,
    OptimTurpeV2Etude,
    OptimTurpeV2EtudeResponse,
    OptimTurpeV2Inputs,
    OptimTurpeV2ListResponse,
    OptimTurpeV2Perimetre,
    OptimTurpeV2Serie,
} from "@modules/courtage/types/optim-turpe-v2"

/**
 * Corps de lancement v2 (overrides leviers self-service) —
 * POST `/api/metier/v5/optimturpe/v2/source/:id`. Le moteur courtage résout les
 * métadonnées source server-side ; on ne transmet que période + corrections.
 */
export type OptimTurpeV2RunBody = {
    date_from?: string
    date_to?: string
    _id_building?: string
    comptage?: OptimTurpeV2Inputs["comptage"]
    compteur?: OptimTurpeV2Inputs["compteur"]
    structure_tarifaire?: OptimTurpeV2Inputs["structure_tarifaire"]
    optimisation?: OptimTurpeV2Inputs["optimisation"]
}

/**
 * Ce que le lancement d'une étude rend — un ÉTAT, jamais un document dont
 * l'appelant devrait deviner s'il est fini (DEV-1529).
 *
 * `termine` : le moteur a calculé dans la requête, l'étude porte son résultat.
 * `en_cours` : le calcul est en file sur le worker courtage (202). L'étude
 * rendue est l'étude PRÉ-ALLOUÉE : elle n'a ni économie ni coûts, ce n'est pas
 * un résultat. `analyseId` sert à relire son état.
 */
export type OptimTurpeV2RunResult =
    | { etat: "termine"; etude: OptimTurpeV2Etude | null }
    | {
          etat: "en_cours"
          analyseId: string | null
          etude: OptimTurpeV2Etude | null
      }

/** Enveloppe servie par la façade v2 sur le lancement (DEV-1529). */
type OptimTurpeV2RunResponse = OptimTurpeV2EtudeResponse & {
    etat?: "termine" | "en_cours"
    _id_analyse?: string | null
}

/** Enveloppe de `GET /v5/optimturpe/v2/analyses/cycle` (DEV-2065). */
type OptimTurpeV2CycleListResponse = {
    data?: OptimTurpeV2Etude[]
    cycles?: Array<OptimTurpeV2Cycle | null>
    tronquee?: boolean | null
}

/** Ce que rend la liste du cycle d'un compteur : les études, et la troncature DITE. */
export type OptimTurpeV2ListeCycle = {
    etudes: OptimTurpeV2Etude[]
    /** `null` = la façade ne l'a pas dit ; `true` = la liste a été coupée par la limite. */
    tronquee: boolean | null
}

/**
 * Lit la réponse d'un lancement (source ou rejeu) en ÉTAT, même règle partout
 * (DEV-1529) : `en_cours` sur un 202 ou un `etat` servi, `termine` sinon.
 */
function lireLancement(reponse: {
    status: number
    data?: OptimTurpeV2RunResponse
}): OptimTurpeV2RunResult {
    const etude = adaptOptimTurpeV2Etude(reponse.data?.data) ?? null
    const enFile = reponse.data?.etat === "en_cours" || reponse.status === 202
    if (!enFile) return { etat: "termine", etude }
    return {
        etat: "en_cours",
        analyseId: reponse.data?._id_analyse ?? etude?._id ?? null,
        etude,
    }
}

/**
 * Client API métier de l'Optim TURPE **v2** (moteur courtage, DEV-715).
 * Distinct de `OptimTurpeService` (legacy data) ; routes `/v5/optimturpe/v2/*`.
 */
export class OptimTurpeV2Service {
    /**
     * Lance une étude v2 pour une source et rend l'ÉTAT du calcul.
     *
     * L'état est lu dans le corps (`etat`), avec le statut HTTP en second témoin :
     * une façade antérieure à ce lot ne pose pas `etat` et répond 200, ce qui
     * donne `termine`, c'est à dire le comportement d'aujourd'hui. C'est ce qui
     * permet de déployer api-metier AVANT le front sans rien casser, dans l'ordre
     * prévu (api-metier, puis front, puis courtage).
     */
    async runBySource(
        sourceId: string,
        body: OptimTurpeV2RunBody,
        controller?: AbortController
    ): Promise<OptimTurpeV2RunResult> {
        return handleServiceCall<OptimTurpeV2RunResult>(async () => {
            const reponse = await axios.post<OptimTurpeV2RunResponse>(
                `/api/metier/v5/optimturpe/v2/source/${sourceId}`,
                body,
                { signal: controller?.signal }
            )
            return lireLancement(reponse)
        })
    }

    /**
     * Déclenche/rafraîchit la narration `explain` (confinée, hash-cache) d'une étude — DEV-715.
     * POST `/api/metier/v5/optimturpe/v2/analyse/:id/explain` (la façade injecte la company).
     */
    async explainEtude(
        analyseId: string,
        controller?: AbortController
    ): Promise<OptimTurpeV2Etude | undefined> {
        return handleServiceCall<OptimTurpeV2Etude | undefined>(async () => {
            const { data } = await axios.post<OptimTurpeV2EtudeResponse>(
                `/api/metier/v5/optimturpe/v2/analyse/${analyseId}/explain`,
                null,
                { signal: controller?.signal }
            )
            return adaptOptimTurpeV2Etude(data?.data) ?? undefined
        })
    }

    /**
     * Récupère la série d'appels de puissance (« preuve par la courbe de charge », DEV-789) d'une
     * étude — recalculée à la demande côté courtage, JAMAIS persistée. GET `…/analyse/:id/serie`
     * (la façade injecte la company). Donnée d'affichage hors contrat du run.
     */
    async getSerie(
        analyseId: string,
        controller?: AbortController
    ): Promise<OptimTurpeV2Serie | undefined> {
        return handleServiceCall<OptimTurpeV2Serie | undefined>(async () => {
            const { data } = await axios.get<{ data?: OptimTurpeV2Serie }>(
                `/api/metier/v5/optimturpe/v2/analyse/${analyseId}/serie`,
                { signal: controller?.signal }
            )
            return data?.data ?? undefined
        })
    }

    /**
     * Demande le rapport PDF d'une étude et renvoie l'URL S3 signée du document (DEV-1914).
     *
     * Le navigateur ne compose plus rien : il n'envoie ni HTML, ni montant, ni nom de fichier.
     * Le document est composé côté courtage à partir d'un payload versionné, rendu en PDF A4
     * par le service `rapport` et déposé sur S3 sous une clé préfixée par la société. La
     * façade api-metier scope la company (404 si l'étude n'appartient pas à l'appelant).
     *
     * Avant DEV-1914 cette méthode envoyait un `template` HTML construit par le front, dont
     * les chiffres étaient recomposés dans le navigateur : deux sources de vérité pour un même
     * montant, et un HTML client rendu comme gabarit côté serveur.
     */
    async getReportPdf(
        analyseId: string,
        controller?: AbortController
    ): Promise<string | undefined> {
        return handleServiceCall<string | undefined>(async () => {
            const { data } = await axios.post<{ data?: { url?: string } }>(
                `/api/metier/v5/optimturpe/v2/analyse/${analyseId}/rapport`,
                undefined,
                { signal: controller?.signal }
            )
            return data?.data?.url ?? undefined
        })
    }

    /** Récupère une étude persistée (scopée company côté api-metier). */
    async getEtude(
        analyseId: string,
        controller?: AbortController
    ): Promise<OptimTurpeV2Etude | undefined> {
        return handleServiceCall<OptimTurpeV2Etude | undefined>(async () => {
            const { data } = await axios.get<OptimTurpeV2EtudeResponse>(
                `/api/metier/v5/optimturpe/v2/analyse/${analyseId}`,
                { signal: controller?.signal }
            )
            const etude = adaptOptimTurpeV2Etude(data?.data) ?? undefined
            // Lot 4g (api-metier#1607, en relecture) : le titulaire et
            // l'adresse du site, servis à côté de l'étude, pour la ligne
            // « Site : » du courriel. Absents, le courriel garde ses crochets.
            const site = data?.site
            if (etude && site && typeof site === "object")
                etude.site_du_courriel = {
                    titulaire:
                        typeof site.titulaire === "string"
                            ? site.titulaire
                            : null,
                    adresse:
                        typeof site.adresse === "string" ? site.adresse : null,
                }
            return etude
        })
    }

    /** Liste les études de la company (historique + dashboard). */
    async listEtudes(
        controller?: AbortController
    ): Promise<OptimTurpeV2Etude[]> {
        return handleServiceCall<OptimTurpeV2Etude[]>(async () => {
            const { data } = await axios.get<OptimTurpeV2ListResponse>(
                `/api/metier/v5/optimturpe/v2/analyses`,
                { signal: controller?.signal }
            )
            return (data?.data ?? [])
                .map((e) => adaptOptimTurpeV2Etude(e))
                .filter((e): e is OptimTurpeV2Etude => e !== null)
        })
    }

    /**
     * Le PÉRIMÈTRE servi par le moteur (DEV-1912) : les segments couverts, les
     * équivalences, les populations hors périmètre et leurs causes.
     *
     * La page cesse ainsi de recopier la règle d'éligibilité — c'est la divergence
     * entre sa recopie (« segment non vide ») et son propre libellé (« C5/C4/C2 »)
     * qui rendait un C1 atteignable au clic (DEV-1678).
     *
     * ⚠️ La route de façade `GET /api/metier/v5/optimturpe/v2/perimetre` **n'existe
     * pas encore** : elle relaiera `GET /optimturpe/perimetre` du moteur, qui arrive
     * avec courtage#229. Tant qu'elle répond 404, cette méthode rend `undefined` et
     * la page dit qu'elle ne connaît pas le périmètre, au lieu de présumer un
     * verdict. Le besoin est porté par la PR ; ce lot ne le tranche pas.
     */
    async getPerimetre(
        controller?: AbortController
    ): Promise<OptimTurpeV2Perimetre | undefined> {
        return handleServiceCall<OptimTurpeV2Perimetre | undefined>(
            async () => {
                const { data } = await axios.get<{
                    data?: OptimTurpeV2Perimetre
                }>(`/api/metier/v5/optimturpe/v2/perimetre`, {
                    signal: controller?.signal,
                })
                return data?.data ?? undefined
            }
        )
    }

    /**
     * Les compteurs ÉTUDIÉS d'un bâtiment, du plus récemment étudié au plus
     * ancien, sans doublon (DEV-2311, revue du 08/10, R4).
     *
     * Même route que l'historique, filtrée par bâtiment : la vue `courantes`
     * est servie par date de création décroissante, études en calcul
     * comprises, études supprimées exclues. La page s'en sert pour rouvrir,
     * sans `?idsource=`, le compteur que le client vient d'étudier plutôt que
     * le premier compteur du bâtiment.
     */
    async listSourcesEtudiees(
        buildingId: string,
        controller?: AbortController
    ): Promise<string[]> {
        return handleServiceCall<string[]>(async () => {
            const { data } = await axios.get<OptimTurpeV2CycleListResponse>(
                `/api/metier/v5/optimturpe/v2/analyses/cycle`,
                {
                    params: {
                        _id_building: buildingId,
                        vue: "courantes",
                        limit: 50,
                    },
                    signal: controller?.signal,
                }
            )
            const ids: string[] = []
            for (const brute of data?.data ?? []) {
                const id =
                    adaptOptimTurpeV2Etude(brute)?.inputs?.source?._id_source
                if (id && !ids.includes(String(id))) ids.push(String(id))
            }
            return ids
        })
    }

    /**
     * L'étude la plus récente du compte, tous bâtiments confondus : son
     * bâtiment et sa date, ou `null` (DEV-2311, revue du 08/10, R4).
     *
     * Même route que l'historique, sans filtre de bâtiment : la façade dérive la
     * société de l'utilisateur, la vue `courantes` est servie par date de
     * création décroissante, études supprimées exclues. Une seule ligne lue.
     */
    async derniereEtudeDuCompte(
        controller?: AbortController
    ): Promise<{ buildingId: string; creeeLe: string | null } | null> {
        return handleServiceCall(async () => {
            const { data } = await axios.get<OptimTurpeV2CycleListResponse>(
                `/api/metier/v5/optimturpe/v2/analyses/cycle`,
                {
                    params: { vue: "courantes", limit: 1 },
                    signal: controller?.signal,
                }
            )
            const etude = adaptOptimTurpeV2Etude(data?.data?.[0])
            if (!etude?._id_building) return null
            return {
                buildingId: String(etude._id_building),
                creeeLe: etude.created_at ?? null,
            }
        })
    }

    /** Le nom d'un bâtiment, lu sans ses enfants, ou `null` (R4). */
    async nomDuBatiment(
        buildingId: string,
        controller?: AbortController
    ): Promise<string | null> {
        return handleServiceCall(async () => {
            const { data } = await axios.get<{ name?: string }>(
                `/api/metier/building/${buildingId}`,
                { params: { children: false }, signal: controller?.signal }
            )
            const nom = typeof data?.name === "string" ? data.name.trim() : ""
            return nom || null
        })
    }

    /**
     * Les études du CYCLE d'un compteur, toutes les chaînes (DEV-2065, DEV-2150).
     *
     * `vue=chaines` rend aussi les études remplacées par un rejeu, pour qu'elles
     * restent lisibles et marquées « remplacée le … ». Les études supprimées
     * logiquement n'y sont pas. Le résumé du cycle servi à côté de chaque étude
     * (`cycles[i]`) complète une étude sans bloc `cycle`, avec `presume` qui dit
     * que son origine est lue par repli.
     */
    async listCycle(
        sourceId: string,
        controller?: AbortController
    ): Promise<OptimTurpeV2ListeCycle> {
        return handleServiceCall<OptimTurpeV2ListeCycle>(async () => {
            const { data } = await axios.get<OptimTurpeV2CycleListResponse>(
                `/api/metier/v5/optimturpe/v2/analyses/cycle`,
                {
                    params: { _id_source: sourceId, vue: "chaines" },
                    signal: controller?.signal,
                }
            )
            const resumes = data?.cycles ?? []
            const etudes = (data?.data ?? [])
                .map((brute, i) => {
                    const etude = adaptOptimTurpeV2Etude(brute)
                    if (!etude) return null
                    const resume = resumes[i] ?? null
                    return resume
                        ? {
                              ...etude,
                              cycle: { ...resume, ...(etude.cycle ?? {}) },
                          }
                        : etude
                })
                .filter((e): e is OptimTurpeV2Etude => e !== null)
            return { etudes, tronquee: data?.tronquee ?? null }
        })
    }

    /**
     * REJOUE une étude (DEV-2065) : une étude NEUVE, chaînée, sur les mêmes
     * entrées ; l'ancienne reste lisible, marquée « remplacée le … ». Même
     * réponse que le lancement : un ÉTAT, le calcul pouvant partir en file (202).
     *
     * `surcharges` est un fragment d'`inputs` que le moteur fusionne en
     * profondeur par dessus les entrées héritées (période sous `data`, etc.).
     */
    async rejouerEtude(
        analyseId: string,
        surcharges?: Record<string, unknown> | null,
        controller?: AbortController
    ): Promise<OptimTurpeV2RunResult> {
        return handleServiceCall<OptimTurpeV2RunResult>(async () => {
            const reponse = await axios.post<OptimTurpeV2RunResponse>(
                `/api/metier/v5/optimturpe/v2/analyse/${analyseId}/rejouer`,
                surcharges && Object.keys(surcharges).length > 0
                    ? { surcharges }
                    : {},
                { signal: controller?.signal }
            )
            return lireLancement(reponse)
        })
    }

    /**
     * ENREGISTRE le scénario retenu avec la paire, SANS recalcul (lot 4g, C25,
     * D17 (a)) : `POST /v5/optimturpe/v2/analyse/:id/scenario`, relayé par
     * api-metier au moteur, qui l'écrit sur l'étude et sur sa jumelle. L'étude
     * rendue est la jambe du scénario RETENU, quelle que soit la jambe
     * appelée : c'est elle dont la page lit la demande et la seconde barre.
     */
    async retenirScenario(
        analyseId: string,
        scenario: "sans_depassements" | "avec_depassements",
        controller?: AbortController
    ): Promise<OptimTurpeV2Etude | undefined> {
        return handleServiceCall<OptimTurpeV2Etude | undefined>(async () => {
            const { data } = await axios.post<OptimTurpeV2EtudeResponse>(
                `/api/metier/v5/optimturpe/v2/analyse/${analyseId}/scenario`,
                { scenario },
                { signal: controller?.signal }
            )
            return adaptOptimTurpeV2Etude(data?.data) ?? undefined
        })
    }

    /** RÉTABLIT une étude antérieure comme courante, SANS recalcul (DEV-2065). */
    async retablirEtude(
        analyseId: string,
        controller?: AbortController
    ): Promise<OptimTurpeV2Etude | undefined> {
        return handleServiceCall<OptimTurpeV2Etude | undefined>(async () => {
            const { data } = await axios.post<OptimTurpeV2EtudeResponse>(
                `/api/metier/v5/optimturpe/v2/analyse/${analyseId}/retablir`,
                {},
                { signal: controller?.signal }
            )
            return adaptOptimTurpeV2Etude(data?.data) ?? undefined
        })
    }

    /**
     * Supprime une étude LOGIQUEMENT (DEV-2065) : elle sort des listes du client,
     * le document reste lisible au support pour instruire une contestation.
     *
     * Remplace l'appel à `DELETE /analyse/:id`, qui effaçait le document : le
     * montant servi (parti au rapport ou au webhook) devenait alors introuvable.
     */
    async supprimerEtude(
        analyseId: string,
        controller?: AbortController
    ): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.post(
                `/api/metier/v5/optimturpe/v2/analyse/${analyseId}/supprimer`,
                {},
                { signal: controller?.signal }
            )
        })
    }
}
